import React, { useState, useEffect, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { Spin, Layout, Breadcrumb } from 'antd'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'

// Hooks
import useDebounce from 'hooks/useDebounce'
import useFilters from 'components/filterButton/useFilters'

// Components
import KpiCard from 'components/kpiCard'
import MainLayout from '../../components/mainLayout'
import UniversalBarChart from 'components/charts/UniversalBarChart'
import {
	TOP_5_COLORS,
	TYPE_COLORS,
	PRIORITY_COLORS_MAP,
} from 'components/charts/chartColors'
import { FilterSidebar, FilterButton } from 'components/filterButton'

// Utils
import { formatMinutesToHHMMSS } from 'utils/formatters'
import { processStatusFilters } from 'utils/filters'

// API's
import {
	getDashboardData,
	getCategoriesOptions,
	getTemplatesOptions,
	getUsersOptions,
	getSolicitantAreasOptions,
} from '../../services/demands/demandsServices'

// CSS
import './styles.scss'

const statusOptions = [
	{ label: 'Aguardando Ação', value: 'Aguardando ação' },
	{ label: 'Em andamento', value: 'GROUP_IN_PROGRESS' },
	{ label: 'Concluído', value: 'Concluído' },
	{ label: 'Cancelado', value: 'Cancelado' },
]

const priorityOptions = [
	{ label: 'Urgente', value: 'Urgente' },
	{ label: 'Alta', value: 'Alta' },
	{ label: 'Média', value: 'Média' },
	{ label: 'Baixa', value: 'Baixa' },
]

const DEFAULT_DATE_RANGE = [
	dayjs().subtract(1, 'month').startOf('day'),
	dayjs().endOf('day'),
]

const DemandsDashboard = () => {
	const history = useHistory()
	const user = useSelector(({ session }) => session)

	const [showDateFilter, setShowDateFilter] = useState(false)
	const [data, setData] = useState(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)

	const [search, setSearch] = useState(undefined)
	const debouncedSearch = useDebounce(search)

	const { filters, setFilter } = useFilters({
		dateRange: DEFAULT_DATE_RANGE,
		categories: [],
		status: [],
		priorities: [],
		template: [],
		requester: [],
		responsible: [],
		solicitantArea: [],
	})

	const canViewSensitiveFilters = ['admin', 'legal'].includes(user.perfil)

	useEffect(() => {
		const loadDashboardData = async () => {
			try {
				setLoading(true)

				const startDate = filters.dateRange?.[0]
					? dayjs(filters.dateRange[0]).toISOString()
					: null
				const endDate = filters.dateRange?.[1]
					? dayjs(filters.dateRange[1]).toISOString()
					: null

				if (!startDate || !endDate) {
					setLoading(false)
					return
				}

				const payloadFilters = {
					categories: filters.categories,
					status: processStatusFilters(filters.status),
					priorities: filters.priorities,
					models: filters.template,
					requesters: filters.requester,
					responsibles: filters.responsible,
					solicitantAreas: filters.solicitantArea,
					search: debouncedSearch,
				}

				const responseData = await getDashboardData(
					startDate,
					endDate,
					payloadFilters
				)

				setData(responseData)
				setError(null)
			} catch (err) {
				setError(err.message || 'Erro ao buscar dados.')
			} finally {
				setLoading(false)
			}
		}

		loadDashboardData()
	}, [filters, debouncedSearch])

	const { priorityData, categoryData, templateData, solicitantAreaData } =
		useMemo(() => {
			if (!data)
				return {
					priorityData: [],
					categoryData: [],
					templateData: [],
					solicitantAreaData: [],
				}

			const priorityData = Object.entries(data.priority ?? {}).map(
				([name, total]) => ({ name, total: Number(total) })
			)

			const categoryData = Object.entries(data.category ?? {}).map(
				([name, total]) => ({ name, total: Number(total) })
			)

			const templateData = Object.entries(data.document_templates ?? {})
				.map(([name, total]) => ({ name, total: Number(total) }))
				.sort((a, b) => b.total - a.total)
				.slice(0, 5)

			const solicitantAreaData = Object.entries(data.solicitant_area ?? {})
				.map(([name, total]) => ({ name, total: Number(total) }))
				.sort((a, b) => b.total - a.total)
				.slice(0, 5)

			return { priorityData, categoryData, templateData, solicitantAreaData }
		}, [data])

	const renderSLAContent = () => {
		if (!data) return null

		const companyName =
			typeof user?.company === 'object' ? user?.company?.name : user?.company
		const clientName = data?.cliente || companyName || user?.cliente

		const isSupersim =
			clientName === 'Supersim' ||
			// clientName === 'dev' ||
			data?.dashboard_type === 'supersim' ||
			user?.dashboard_type === 'supersim'

		if (isSupersim) {
			const totalMin = data.sla_average_minutes || 0
			const d = Math.floor(totalMin / 1440)
			const h = Math.floor((totalMin % 1440) / 60)
			const m = Math.floor(totalMin % 60)

			const parts = []
			if (d > 0) parts.push(`${d} ${d === 1 ? 'dia' : 'dias'}`)
			if (h > 0) parts.push(`${h} ${h === 1 ? 'hora' : 'horas'}`)
			if (m > 0) parts.push(`${m} ${m === 1 ? 'minuto' : 'minutos'}`)

			let slaTime = '0 minutos'
			if (parts.length === 1) {
				slaTime = parts[0]
			} else if (parts.length === 2) {
				slaTime = `${parts[0]} e ${parts[1]}`
			} else if (parts.length > 2) {
				slaTime = `${parts[0]}, ${parts[1]} e ${parts[2]}`
			}

			return <KpiCard title="SLA de resposta" value={slaTime} />
		}

		return (
			<KpiCard
				title="SLA de resposta"
				value={formatMinutesToHHMMSS(data.sla_average_minutes || 0)}
			/>
		)
	}

	const renderContent = () => {
		if (loading) {
			return (
				<div className="dashboard-loader-container">
					<Spin size="large" />
				</div>
			)
		}

		if (error) {
			return (
				<div className="dashboard-loader-container">
					<p style={{ color: 'red' }}>Erro: {error}</p>
				</div>
			)
		}

		if (data) {
			const completed = data.status['Concluído'] || 0
			const pending =
				(data.status['Aguardando ação'] || 0) +
				(data.status['Em Validação'] || 0)

			return (
				<div className="dashboard-container">
					<div className="kpi-row">
						<KpiCard
							title="Tarefas concluídas"
							value={String(completed).padStart(3, '0')}
						/>
						<KpiCard
							title="Tarefas pendentes"
							value={String(pending).padStart(3, '0')}
						/>
						{renderSLAContent()}
					</div>

					<div
						className="charts-row-top5"
						style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
						<div style={{ flex: 1 }}>
							<UniversalBarChart
								title="Top 5 modelos mais demandados"
								data={templateData}
								colors={TOP_5_COLORS}
								emptyMessage="Nenhum modelo encontrado."
							/>
						</div>

						<div style={{ flex: 1 }}>
							<UniversalBarChart
								title="Top 5 áreas mais demandantes"
								data={solicitantAreaData}
								colors={TOP_5_COLORS}
								emptyMessage="Nenhuma área encontrada."
							/>
						</div>
					</div>

					<div className="bottom-row">
						<UniversalBarChart
							title="Demanda por urgência"
							data={priorityData}
							colors={PRIORITY_COLORS_MAP}
							emptyMessage="Nenhuma tarefa encontrada neste período."
						/>
						<UniversalBarChart
							title="Demanda por tipo"
							data={categoryData}
							colors={TYPE_COLORS}
							emptyMessage="Nenhuma tarefa encontrada neste período."
						/>
					</div>
				</div>
			)
		}

		return <p>Nenhum dado encontrado.</p>
	}

	const renderFilter = (
		<FilterSidebar>
			<FilterButton
				title="Período"
				filterKey="date_range"
				currentSection="demands"
				type="date"
				isPopover={false}
				selected={filters.dateRange}
				onChange={(val) => {
					const nextVal = val && val.length > 0 ? val : DEFAULT_DATE_RANGE
					setFilter('dateRange', nextVal)
				}}
			/>
			<FilterButton
				title="Categoria"
				filterKey="category"
				currentSection="demands"
				type="checkbox"
				isPopover={false}
				hasAsyncSearch={true}
				asyncFetch={getCategoriesOptions}
				selected={filters.categories}
				onChange={(val) => setFilter('categories', val)}
			/>
			<FilterButton
				title="Status"
				filterKey="status"
				currentSection="demands"
				type="checkbox"
				options={statusOptions}
				isPopover={false}
				selected={filters.status}
				onChange={(val) => setFilter('status', val)}
			/>
			<FilterButton
				title="Prioridade"
				filterKey="priority"
				currentSection="demands"
				type="checkbox"
				options={priorityOptions}
				isPopover={false}
				selected={filters.priorities}
				onChange={(val) => setFilter('priorities', val)}
			/>
			<FilterButton
				title="Modelo"
				filterKey="template"
				currentSection="demands"
				type="checkbox"
				isPopover={false}
				hasAsyncSearch={true}
				asyncFetch={getTemplatesOptions}
				selected={filters.template}
				onChange={(val) => setFilter('template', val)}
			/>
			{canViewSensitiveFilters && (
				<>
					<FilterButton
						title="Solicitante"
						filterKey="requester"
						currentSection="demands"
						type="checkbox"
						isPopover={false}
						hasAsyncSearch={true}
						asyncFetch={getUsersOptions}
						selected={filters.requester}
						onChange={(val) => setFilter('requester', val)}
					/>
					<FilterButton
						title="Responsável"
						filterKey="responsible"
						currentSection="demands"
						type="checkbox"
						isPopover={false}
						hasAsyncSearch={true}
						asyncFetch={getUsersOptions}
						selected={filters.responsible}
						onChange={(val) => setFilter('responsible', val)}
					/>
					<FilterButton
						title="Área Solicitante"
						filterKey="solicitantArea"
						currentSection="demands"
						type="checkbox"
						isPopover={false}
						hasAsyncSearch={true}
						asyncFetch={getSolicitantAreasOptions}
						selected={filters.solicitantArea}
						onChange={(val) => setFilter('solicitantArea', val)}
					/>
				</>
			)}
		</FilterSidebar>
	)

	return (
		<MainLayout>
			<Layout style={{ backgroundColor: '#fff', minHeight: '100%' }}>
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						padding: '16px 24px',
						background: '#fff',
						borderBottom: '1px solid #f0f0f0',
					}}>
					<Breadcrumb>
						<Breadcrumb.Item
							onClick={() => history.push('/demands')}
							style={{ cursor: 'pointer' }}>
							Demandas
						</Breadcrumb.Item>
						<Breadcrumb.Item>Dashboard</Breadcrumb.Item>
					</Breadcrumb>

					<div className="filter-container">{renderFilter}</div>
				</div>

				{renderContent()}
			</Layout>
		</MainLayout>
	)
}

export default DemandsDashboard
